import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen, Search, Plus, Trash2, Edit3, X, Tag } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  grammar: 'bg-[#A2D2FF]',
  punctuation: 'bg-[#FFC8DD]',
  style: 'bg-[#CDB4DB]',
  spelling: 'bg-[#B9FBC0]',
  words: 'bg-[#FDF0D5]',
  other: 'bg-white',
};

function RuleEditor({ existing, categories, onSave, onCancel }) {
  const [title, setTitle] = useState(existing?.title || '');
  const [content, setContent] = useState(existing?.content || '');
  const [category, setCategory] = useState(existing?.category || 'other');
  const [tagsStr, setTagsStr] = useState((existing?.tags || []).join(', '));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim() || !content.trim()) {
      toast.error('Titel och innehåll krävs');
      return;
    }
    setSaving(true);
    try {
      const tags = tagsStr.split(',').map((t) => t.trim()).filter(Boolean);
      await onSave({ title: title.trim(), content: content.trim(), category, tags });
    } finally { setSaving(false); }
  };

  return (
    <div className="neo-card p-4 bg-white dark:bg-[#252545] space-y-3" data-testid="rule-editor">
      <div className="flex items-center justify-between">
        <h4 className="font-bold font-['Fredoka']">{existing ? 'Redigera regel' : 'Lägg till regel'}</h4>
        <button onClick={onCancel} className="neo-btn p-1 bg-white"><X className="w-4 h-4" /></button>
      </div>
      <input
        type="text"
        placeholder="Titel (t.ex. 'Komma före som')"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={120}
        className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
        data-testid="rule-title-input"
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={5}
        maxLength={4000}
        placeholder="Regeln + exempel. Tumregel + undantag + knep för att minnas."
        className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54] resize-none font-mono"
        data-testid="rule-content-input"
      />
      <div>
        <p className="text-xs font-bold mb-1">Kategori</p>
        <div className="flex flex-wrap gap-1">
          {Object.entries(categories).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setCategory(k)}
              type="button"
              className={`neo-btn px-2 py-1 text-xs ${category === k ? `${CATEGORY_COLORS[k] || 'bg-white'} ring-2 ring-black` : 'bg-white'}`}
              data-testid={`rule-cat-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-xs font-bold">Taggar (komma-separerade)</span>
        <input
          type="text"
          value={tagsStr}
          onChange={(e) => setTagsStr(e.target.value)}
          placeholder="t.ex. komma, bisats, citat"
          className="mt-1 w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
          data-testid="rule-tags-input"
        />
      </label>
      <button
        onClick={save}
        disabled={saving}
        className="neo-btn w-full py-2 bg-[#B9FBC0] text-sm font-bold disabled:opacity-50"
        data-testid="rule-save"
      >
        {saving ? 'Sparar…' : existing ? 'Spara' : 'Lägg till'}
      </button>
    </div>
  );
}

function RuleCard({ rule, onEdit, onDelete, highlight }) {
  const [expanded, setExpanded] = useState(false);
  const preview = rule.content.length > 100 ? rule.content.slice(0, 100) + '…' : rule.content;
  const catColor = CATEGORY_COLORS[rule.category] || 'bg-white';

  const highlightText = (text) => {
    if (!highlight) return text;
    const regex = new RegExp(`(${highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((p, i) =>
      regex.test(p) ? <mark key={i} className="bg-[#FFE8A3] px-0.5">{p}</mark> : p
    );
  };

  return (
    <div className="neo-card bg-white dark:bg-[#252545] overflow-hidden" data-testid={`rule-${rule.id}`}>
      <button
        onClick={() => setExpanded((e) => !e)}
        className="w-full p-4 text-left"
      >
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div className="flex-1 min-w-0">
            <h3 className="font-bold font-['Fredoka'] text-base">{highlightText(rule.title)}</h3>
            {!expanded && (
              <p className="text-xs opacity-70 mt-0.5 line-clamp-2">{highlightText(preview)}</p>
            )}
          </div>
          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black ${catColor} whitespace-nowrap`}>
            {rule.category}
          </span>
        </div>
        {rule.tags && rule.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {rule.tags.map((t) => (
              <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FDF0D5] border border-black">
                #{t}
              </span>
            ))}
          </div>
        )}
      </button>
      {expanded && (
        <div className="border-t-2 border-black p-4 bg-[#FFFAF0] dark:bg-[#1f1f3a] space-y-3">
          <p className="text-sm whitespace-pre-wrap leading-relaxed">{highlightText(rule.content)}</p>
          <div className="flex gap-2 justify-end">
            <button onClick={() => onEdit(rule)} className="neo-btn px-3 py-1.5 bg-white text-xs font-bold flex items-center gap-1" data-testid={`rule-edit-${rule.id}`}>
              <Edit3 className="w-3 h-3" /> Redigera
            </button>
            <button onClick={() => onDelete(rule)} className="neo-btn px-3 py-1.5 bg-white hover:bg-red-100 text-xs font-bold flex items-center gap-1" data-testid={`rule-delete-${rule.id}`}>
              <Trash2 className="w-3 h-3" /> Ta bort
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WritingRulesPage() {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('');
  const [editing, setEditing] = useState(null); // rule | 'new' | null
  const [debounced, setDebounced] = useState('');

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchRules = useCallback(async () => {
    try {
      const params = [];
      if (debounced) params.push(`q=${encodeURIComponent(debounced)}`);
      if (activeCat) params.push(`category=${activeCat}`);
      const url = `${API}/writing-rules${params.length ? '?' + params.join('&') : ''}`;
      const { data } = await axios.get(url, { withCredentials: true });
      setRules(data.items || []);
      setCategories(data.categories || {});
    } catch { /* noop */ }
  }, [debounced, activeCat]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchRules();
      setLoading(false);
    })();
  }, [fetchRules]);

  const save = async (payload) => {
    try {
      if (editing && editing !== 'new') {
        await axios.patch(`${API}/writing-rules/${editing.id}`, payload, { withCredentials: true });
        toast.success('Regel uppdaterad');
      } else {
        await axios.post(`${API}/writing-rules`, payload, { withCredentials: true });
        toast.success('Regel tillagd ✨');
      }
      setEditing(null);
      await fetchRules();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte spara'); }
  };

  const del = async (rule) => {
    if (!window.confirm(`Ta bort "${rule.title}"?`)) return;
    try {
      await axios.delete(`${API}/writing-rules/${rule.id}`, { withCredentials: true });
      await fetchRules();
      toast.success('Borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  return (
    <div className="space-y-4" data-testid="writing-rules-page">
      <div>
        <h2 className="text-2xl font-bold font-['Fredoka'] flex items-center gap-2">
          <BookOpen className="w-6 h-6" /> Skrivregler
        </h2>
        <p className="text-xs opacity-75 mt-1">Din egen svenska ordbok — sök när du är osäker ✍️</p>
      </div>

      {/* Search + add */}
      <div className="neo-card p-3 bg-white dark:bg-[#252545]">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px] px-3 py-2 rounded-xl border-2 border-black bg-white dark:bg-[#2d2d54]">
            <Search className="w-4 h-4 opacity-60" />
            <input
              type="text"
              placeholder="Sök titel, innehåll eller tagg…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              data-testid="rules-search-input"
            />
            {search && (
              <button onClick={() => setSearch('')} className="opacity-60 hover:opacity-100" data-testid="rules-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <button
            onClick={() => setEditing('new')}
            className="neo-btn px-3 py-2 bg-[#B9FBC0] text-sm font-bold flex items-center gap-1"
            data-testid="rules-add-btn"
          >
            <Plus className="w-4 h-4" /> Ny regel
          </button>
        </div>

        {/* Category filter */}
        <div className="flex flex-wrap gap-1 mt-3" data-testid="rules-cat-filter">
          <button
            onClick={() => setActiveCat('')}
            className={`neo-btn px-2 py-1 text-xs ${activeCat === '' ? 'bg-[#CDB4DB] ring-2 ring-black' : 'bg-white'}`}
            data-testid="rules-cat-all"
          >
            Alla
          </button>
          {Object.entries(categories).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setActiveCat(k)}
              className={`neo-btn px-2 py-1 text-xs flex items-center gap-1 ${activeCat === k ? `${CATEGORY_COLORS[k] || 'bg-white'} ring-2 ring-black` : 'bg-white'}`}
              data-testid={`rules-cat-${k}`}
            >
              <Tag className="w-3 h-3" /> {label}
            </button>
          ))}
        </div>
      </div>

      {editing && (
        <RuleEditor
          existing={editing === 'new' ? null : editing}
          categories={categories}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Rules list */}
      {loading ? (
        <div className="neo-card p-8 text-center animate-pulse-soft">Laddar regler…</div>
      ) : rules.length === 0 ? (
        <div className="neo-card p-8 text-center bg-white dark:bg-[#252545]" data-testid="rules-empty">
          <BookOpen className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="font-bold">Ingen regel matchar</p>
          <p className="text-xs opacity-70 mt-1">Ändra sökord eller lägg till en ny regel.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="rules-list">
          <p className="text-xs opacity-60">{rules.length} regel{rules.length === 1 ? '' : 'er'}</p>
          {rules.map((r) => (
            <RuleCard key={r.id} rule={r} onEdit={setEditing} onDelete={del} highlight={debounced} />
          ))}
        </div>
      )}
    </div>
  );
}
